// client/src/components/publishing/BestTimeSuggestions.jsx
//
// "Suggested times" chips for the Schedule Post composer (Roadmap Phase 3.1).
// The server works them out from this organization's own post engagement for
// the selected platforms, falling back to a common weekly pattern while there
// is little history (server/services/bestTime.js). Times are in the
// organization's time zone, the same zone as the composer's date / time
// fields, so picking one just fills those fields.

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegClock } from 'react-icons/fa';
import { getBestTimes } from '../../services/publishingApi';
import './Publishing.css';

/** 'YYYY-MM-DD' + 'HH:MM' (wall time) -> "Tue, Oct 27 · 9:00 AM" in the user's language. */
export function slotLabel(date, time, locale) {
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  const asUtc = new Date(Date.UTC(y, m - 1, d, hh, mm));
  const day = asUtc.toLocaleDateString(locale, { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' });
  const clock = asUtc.toLocaleTimeString(locale, { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' });
  return `${day} · ${clock}`;
}

export default function BestTimeSuggestions({ tenantId, platforms, onPick, selected }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const key = (platforms || []).slice().sort().join(',');

  useEffect(() => {
    if (!tenantId) return undefined;
    let live = true;
    const timer = setTimeout(() => {
      getBestTimes(tenantId, key ? key.split(',') : [])
        .then((r) => { if (live) { setData(r); setFailed(false); } })
        .catch(() => { if (live) setFailed(true); });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [tenantId, key]);

  if (failed || !data || !data.slots || !data.slots.length) return null;

  const basis = {
    history: t('publishing.bestTime.fromHistory', 'Based on when your posts got the most engagement.'),
    blended: t('publishing.bestTime.blended', 'Based on your posts so far plus common patterns. They get sharper as you post more.'),
    default: t('publishing.bestTime.default', 'Common good times to start with. Once you have published a few posts on these platforms, suggestions come from your own results.'),
  }[data.source] || '';

  return (
    <div className="pb-besttime" data-testid="best-times">
      <div className="pb-besttime-head">
        <FaRegClock aria-hidden="true" />
        <strong>{t('publishing.bestTime.title', 'Suggested times')}</strong>
      </div>
      <div className="pb-chips" role="group" aria-label={t('publishing.bestTime.title', 'Suggested times')}>
        {data.slots.map((s) => {
          const on = selected && selected.date === s.date && selected.time === s.time;
          return (
            <button
              key={s.utc}
              type="button"
              className={`pb-chip${on ? ' is-on' : ''}`}
              aria-pressed={on}
              onClick={() => onPick({ date: s.date, time: s.time })}
            >
              {slotLabel(s.date, s.time, i18n.language)}
            </button>
          );
        })}
      </div>
      <p className="pb-besttime-note">{basis}</p>
    </div>
  );
}
